/* Somber — Photo gallery: fade-in on scroll + full-screen lightbox
   (arrow keys, swipe, wraps around, returns focus on close). */
if (!customElements.get('gallery-somber')) {
  customElements.define(
    'gallery-somber',
    class GallerySomber extends HTMLElement {
      connectedCallback() {
        this.buttons = Array.from(this.querySelectorAll('.gallery-somber__open'));
        this.dialog = this.querySelector('.gallery-somber__lightbox');
        if (!this.dialog || !this.buttons.length) return;

        this.full = this.dialog.querySelector('.gallery-somber__full');
        this.captionText = this.dialog.querySelector('.gallery-somber__caption-text');
        this.date = this.dialog.querySelector('.gallery-somber__date');
        this.position = this.dialog.querySelector('.gallery-somber__index');
        this.index = 0;

        this.buttons.forEach((button, i) => button.addEventListener('click', () => this.open(i)));
        this.dialog.querySelector('[data-gallery-prev]').addEventListener('click', () => this.step(-1));
        this.dialog.querySelector('[data-gallery-next]').addEventListener('click', () => this.step(1));
        this.dialog.querySelector('[data-gallery-close]').addEventListener('click', () => this.dialog.close());

        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.step(-1);
          if (event.key === 'ArrowRight') this.step(1);
        });

        // click on the dark surround (not the photo or buttons) closes
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog || event.target.classList.contains('gallery-somber__figure')) {
            this.dialog.close();
          }
        });

        let startX = null;
        let startY = null;
        this.dialog.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) return;
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
          },
          { passive: true }
        );
        this.dialog.addEventListener('touchend', (event) => {
          if (startX === null) return;
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - startY;
          startX = null;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.step(dx < 0 ? 1 : -1);
        });

        this.reveal();

        this.grid = this.querySelector('.gallery-somber__grid--collage');
        if (this.grid) {
          this.pack();
          this.media = window.matchMedia('(min-width: 750px)');
          this.media.addEventListener('change', () => this.pack());
        }
      }

      /* Collage gap filler. Replays the grid's "dense" placement, then
         stretches a neighbouring photo into every empty cell (including
         the ragged bottom edge) and pins each photo to its final spot. */
      pack() {
        const items = Array.from(this.grid.children);
        items.forEach((item) => {
          item.style.gridColumn = '';
          item.style.gridRow = '';
        });

        const cols = parseInt(getComputedStyle(this.grid).getPropertyValue('--cols'), 10) || 2;
        const span = (value) => parseInt(String(value).replace('span', ''), 10) || 1;
        const tiles = items.map((item) => {
          const style = getComputedStyle(item);
          return { item, c: Math.min(span(style.gridColumnStart), cols), r: span(style.gridRowStart), x: 0, y: 0 };
        });

        // grid[y][x] = tile index or -1
        const grid = [];
        const row = (y) => grid[y] || (grid[y] = new Array(cols).fill(-1));
        const free = (x, y, c, r) => {
          for (let j = y; j < y + r; j++) for (let i = x; i < x + c; i++) if (i >= cols || row(j)[i] !== -1) return false;
          return true;
        };
        const fill = (t, n) => {
          for (let j = t.y; j < t.y + t.r; j++) for (let i = t.x; i < t.x + t.c; i++) row(j)[i] = n;
        };

        tiles.forEach((t, n) => {
          for (let y = 0; ; y++) {
            for (let x = 0; x + t.c <= cols; x++) {
              if (free(x, y, t.c, t.r)) {
                t.x = x;
                t.y = y;
                fill(t, n);
                return;
              }
            }
          }
        });

        const height = Math.max(...tiles.map((t) => t.y + t.r));
        const at = (x, y) => (y >= 0 && y < height && x >= 0 && x < cols ? row(y)[x] : -1);

        let changed = true;
        while (changed) {
          changed = false;
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < cols; x++) {
              if (row(y)[x] !== -1) continue;
              const above = tiles[at(x, y - 1)];
              const left = tiles[at(x - 1, y)];
              const right = tiles[at(x + 1, y)];
              const below = tiles[at(x, y + 1)];
              if (above && free(above.x, y, above.c, 1)) {
                above.r += 1;
              } else if (left && free(x, left.y, 1, left.r)) {
                left.c += 1;
              } else if (right && free(x, right.y, 1, right.r)) {
                right.x -= 1;
                right.c += 1;
              } else if (below && free(below.x, y, below.c, 1)) {
                below.y -= 1;
                below.r += 1;
              } else {
                continue;
              }
              tiles.forEach((t, n) => fill(t, n));
              changed = true;
            }
          }
        }

        tiles.forEach((t) => {
          t.item.style.gridColumn = `${t.x + 1} / span ${t.c}`;
          t.item.style.gridRow = `${t.y + 1} / span ${t.r}`;
        });
      }

      open(index) {
        this.show(index);
        this.dialog.showModal();
      }

      step(delta) {
        const total = this.buttons.length;
        this.show((this.index + delta + total) % total);
      }

      show(index) {
        this.index = index;
        const button = this.buttons[index];
        const thumb = button.querySelector('img');

        // show the already-loaded thumbnail straight away, then swap in
        // the sharp version once it has downloaded
        this.full.width = button.dataset.width;
        this.full.height = button.dataset.height;
        this.full.style.setProperty('--gallery-ratio', button.dataset.width / button.dataset.height);
        this.full.alt = thumb ? thumb.alt : '';
        this.full.src = (thumb && thumb.currentSrc) || button.dataset.full;
        const sharp = new Image();
        sharp.onload = () => {
          if (this.index === index) this.full.src = sharp.src;
        };
        sharp.src = button.dataset.full;

        this.captionText.textContent = button.dataset.class || '';
        this.date.textContent = button.dataset.date || '';
        this.position.textContent = `${index + 1} / ${this.buttons.length}`;

        // warm up the neighbours so arrowing feels instant
        [-1, 1].forEach((d) => {
          const next = this.buttons[(index + d + this.buttons.length) % this.buttons.length];
          new Image().src = next.dataset.full;
        });
      }

      reveal() {
        const still =
          document.documentElement.classList.contains('a11y-still') ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (still || !('IntersectionObserver' in window)) return;

        const items = Array.from(this.querySelectorAll('.gallery-somber__item'));
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-shown');
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px' }
        );
        items.forEach((item) => {
          // only animate photos that start below the fold
          if (item.getBoundingClientRect().top < window.innerHeight) return;
          item.classList.add('is-pending');
          observer.observe(item);
        });
      }
    }
  );
}
